import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import Input from '../ui/Input';
import Badge from '../ui/Badge';
import { blogService, SeoAnalysisRow, SeoPageAnalysis, SeoActionPlan, SeoComparison, SeoRecommendation } from '../../services/blogService';
import SiteAuditPage from '../../pages/competitors/SiteAuditPage';
import { Gauge, Search, Sparkles, Swords, FileText, Link2, Image as ImageIcon, ShieldCheck, Smartphone, Code2, Trophy, AlertTriangle, Target, Zap, Plus, X, Loader2, CheckCircle, XCircle, TrendingUp, History, Trash2, Clock, RefreshCw } from 'lucide-react';

type SubTab = 'audit' | 'analysis';

const errMsg = (err: any, fallback: string): string =>
  err?.response?.data?.message ?? err?.message ?? fallback;

const scoreColor = (score: number): string =>
  score >= 80 ? 'text-green-500' : score >= 50 ? 'text-yellow-500' : 'text-red-500';

const scoreRing = (score: number): string =>
  score >= 80 ? 'stroke-green-500' : score >= 50 ? 'stroke-yellow-500' : 'stroke-red-500';

const scorePillCls = (score: number): string =>
  score >= 80
    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    : score >= 50
      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';

const fmt = (n?: number): string => (n ?? 0).toLocaleString('pt-BR');
const fmtMs = (n?: number): string => `${(n ?? 0).toLocaleString('pt-BR')} ms`;

const truncateUrl = (url: string, max = 28): string => {
  const clean = (url ?? '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  return clean.length > max ? clean.slice(0, max - 1) + '…' : clean;
};

const fmtDate = (iso?: string): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
};

// ===================== Score Ring =====================
const ScoreRing: React.FC<{ score: number; label: string }> = ({ score, label }) => {
  const radius = 52;
  const circ = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, score ?? 0));
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="relative flex items-center justify-center w-36 h-36 shrink-0">
      <svg className="w-36 h-36 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={radius} className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="10" fill="none" />
        <circle
          cx="60" cy="60" r={radius}
          className={scoreRing(score ?? 0)}
          strokeWidth="10" fill="none" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-4xl font-bold ${scoreColor(score ?? 0)}`}>{Math.round(pct)}</span>
        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</span>
      </div>
    </div>
  );
};

// ===================== Signal Card =====================
const SignalCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  good?: boolean;
}> = ({ icon, label, value, good }) => (
  <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-800/50 p-4">
    <div className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${good === undefined ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300' : good ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'}`}>
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</div>
      <div className="text-base font-semibold text-gray-900 dark:text-white truncate">{value}</div>
    </div>
    {good !== undefined && (
      good
        ? <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
        : <XCircle className="w-5 h-5 text-red-500 shrink-0" />
    )}
  </div>
);

const POLL_MS = 3000;

// ===================== Persisted-analysis hook =====================
// Drives all state from the backend so results survive tab switches / navigation.
function useSeoAnalyses(type: 'site' | 'compare', tFallback: (key: string, fb: string) => string) {
  const [history, setHistory] = useState<SeoAnalysisRow[]>([]);
  const [selected, setSelected] = useState<SeoAnalysisRow | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const selectedIdRef = useRef<string | null>(null);

  selectedIdRef.current = selected?.id ?? null;

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const upsertHistory = useCallback((row: SeoAnalysisRow) => {
    setHistory((prev) => {
      const idx = prev.findIndex((r) => r.id === row.id);
      if (idx === -1) return [row, ...prev];
      const next = [...prev];
      // keep lightweight history rows (no .result) but update status/score
      next[idx] = { ...next[idx], ...row, result: next[idx].result };
      return next;
    });
  }, []);

  const startPoll = useCallback((id: string) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const res = await blogService.getSeoAnalysis(id);
        const row = res.data;
        upsertHistory({ ...row, result: undefined });
        if (selectedIdRef.current === id) setSelected(row);
        if (row.status !== 'running') stopPoll();
      } catch {
        stopPoll();
      }
    }, POLL_MS);
  }, [stopPoll, upsertHistory]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await blogService.listSeoAnalyses(type);
      const rows = res.data ?? [];
      setHistory(rows);
      // resume polling for the first running row (and select it if nothing selected)
      const running = rows.find((r) => r.status === 'running');
      if (running) {
        if (!selectedIdRef.current) {
          try {
            const full = await blogService.getSeoAnalysis(running.id);
            setSelected(full.data);
          } catch {
            setSelected(running);
          }
        }
        startPoll(running.id);
      }
    } catch (err: any) {
      toast.error(errMsg(err, tFallback('blogSeo.seo.errLoadHistory', 'Erro ao carregar histórico')));
    } finally {
      setLoadingHistory(false);
    }
  }, [type, startPoll, tFallback]);

  const select = useCallback(async (id: string) => {
    try {
      const res = await blogService.getSeoAnalysis(id);
      setSelected(res.data);
      upsertHistory({ ...res.data, result: undefined });
      if (res.data.status === 'running') startPoll(id);
      else stopPoll();
    } catch (err: any) {
      toast.error(errMsg(err, tFallback('blogSeo.seo.errGeneric', 'Erro')));
    }
  }, [startPoll, stopPoll, upsertHistory, tFallback]);

  const remove = useCallback(async (id: string) => {
    try {
      await blogService.deleteSeoAnalysis(id);
      if (selectedIdRef.current === id) {
        setSelected(null);
        stopPoll();
      }
      await loadHistory();
      toast.success(tFallback('blogSeo.seo.deleted', 'Análise removida'));
    } catch (err: any) {
      toast.error(errMsg(err, tFallback('blogSeo.seo.errGeneric', 'Erro')));
    }
  }, [loadHistory, stopPoll, tFallback]);

  // submitFn returns the created row (status 'running')
  const submit = useCallback(async (submitFn: () => Promise<{ data: SeoAnalysisRow }>) => {
    setSubmitting(true);
    try {
      const res = await submitFn();
      const row = res.data;
      setSelected(row);
      setHistory((prev) => [{ ...row, result: undefined }, ...prev.filter((r) => r.id !== row.id)]);
      startPoll(row.id);
      return true;
    } catch (err: any) {
      toast.error(errMsg(err, tFallback('blogSeo.seo.errGeneric', 'Erro')));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [startPoll, tFallback]);

  useEffect(() => {
    loadHistory();
    return () => stopPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { history, selected, setSelected, loadingHistory, submitting, loadHistory, select, remove, submit };
}

// ===================== History list =====================
const HistoryList: React.FC<{
  rows: SeoAnalysisRow[];
  selectedId?: string | null;
  loading: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRefresh: () => void;
  emptyLabel: string;
}> = ({ rows, selectedId, loading, onSelect, onDelete, onRefresh, emptyLabel }) => {
  const { t } = useTranslation();
  return (
    <Card className="lg:sticky lg:top-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-primary-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('blogSeo.seo.historyTitle', 'Histórico')}
          </h3>
        </div>
        <button
          onClick={onRefresh}
          className="text-gray-400 hover:text-primary-500 transition-colors"
          title={t('blogSeo.seo.refresh', 'Atualizar')}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 py-6 text-center">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
          {rows.map((row) => {
            const active = row.id === selectedId;
            return (
              <li key={row.id}>
                <div
                  className={`group flex items-start gap-2 rounded-xl border p-3 cursor-pointer transition-all ${
                    active
                      ? 'border-primary-400 bg-primary-50/60 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60'
                  }`}
                  onClick={() => onSelect(row.id)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {truncateUrl(row.url)}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-400 dark:text-gray-500">
                      <Clock className="w-3 h-3" />
                      {fmtDate(row.created_at)}
                    </div>
                    <div className="mt-2">
                      {row.status === 'running' && (
                        <span className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          {t('blogSeo.seo.statusRunning', 'Analisando...')}
                        </span>
                      )}
                      {row.status === 'completed' && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${scorePillCls(row.score ?? 0)}`}>
                          {fmt(row.score ?? 0)}
                        </span>
                      )}
                      {row.status === 'failed' && (
                        <span className="inline-flex items-center gap-1 text-xs text-red-500">
                          <XCircle className="w-3 h-3" />
                          {t('blogSeo.seo.statusFailed', 'Falhou')}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(row.id); }}
                    className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    title={t('blogSeo.seo.delete', 'Excluir')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

// ===================== States shared by result panels =====================
const RunningPanel: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Card>
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Loader2 className="w-10 h-10 text-primary-500 animate-spin mb-4" />
        <p className="text-base font-medium text-gray-900 dark:text-white">
          {t('blogSeo.seo.runningTitle', 'Analisando em segundo plano...')}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
          {t('blogSeo.seo.runningHint', 'Você pode trocar de aba — a análise continua e o resultado fica salvo.')}
        </p>
      </div>
    </Card>
  );
};

const FailedPanel: React.FC<{ message?: string | null }> = ({ message }) => {
  const { t } = useTranslation();
  return (
    <Card className="border-red-200 dark:border-red-800">
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <XCircle className="w-10 h-10 text-red-500 mb-4" />
        <p className="text-base font-medium text-gray-900 dark:text-white">
          {t('blogSeo.seo.failedTitle', 'A análise falhou')}
        </p>
        <p className="text-sm text-red-500 mt-1 max-w-md">
          {message ?? t('blogSeo.seo.failedGeneric', 'Não foi possível concluir esta análise.')}
        </p>
      </div>
    </Card>
  );
};

const EmptyPanel: React.FC<{ icon: React.ReactNode; title: string; hint: string }> = ({ icon, title, hint }) => (
  <Card>
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 mb-4">
        {icon}
      </div>
      <p className="text-base font-medium text-gray-900 dark:text-white">{title}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">{hint}</p>
    </div>
  </Card>
);

// ===================== Site result =====================
const impactBadge = (impact: SeoRecommendation['impact'], t: (k: string, fb: string) => string) => {
  if (impact === 'high') return <Badge variant="danger">{t('blogSeo.seo.impactHigh', 'Alto')}</Badge>;
  if (impact === 'medium') return <Badge variant="warning">{t('blogSeo.seo.impactMedium', 'Médio')}</Badge>;
  return <Badge variant="gray">{t('blogSeo.seo.impactLow', 'Baixo')}</Badge>;
};
const effortBadge = (effort: SeoRecommendation['effort'], t: (k: string, fb: string) => string) => {
  if (effort === 'low') return <Badge variant="success">{t('blogSeo.seo.effortLow', 'Baixo')}</Badge>;
  if (effort === 'medium') return <Badge variant="warning">{t('blogSeo.seo.effortMedium', 'Médio')}</Badge>;
  return <Badge variant="gray">{t('blogSeo.seo.effortHigh', 'Alto')}</Badge>;
};

const SiteResult: React.FC<{ row: SeoAnalysisRow }> = ({ row }) => {
  const { t } = useTranslation();
  const analysis: SeoPageAnalysis | undefined = row.result?.analysis;
  const aiPlan: SeoActionPlan | null | undefined = row.result?.aiPlan;

  if (!analysis) {
    return (
      <Card>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
          {t('blogSeo.seo.noResult', 'Sem dados de resultado para esta análise.')}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Score + summary */}
      <Card className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800/50 dark:to-gray-900/50">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <ScoreRing score={analysis.score ?? 0} label={t('blogSeo.seo.scoreLabel', 'Score SEO')} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-primary-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('blogSeo.seo.aiSummary', 'Resumo da IA')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {aiPlan?.summary ?? t('blogSeo.seo.noSummary', 'Sem resumo disponível para esta URL.')}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 truncate">{analysis.url}</p>
          </div>
        </div>
      </Card>

      {/* Signals grid */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          {t('blogSeo.seo.signalsTitle', 'Sinais on-page')}
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <SignalCard
            icon={<FileText className="w-5 h-5" />}
            label={t('blogSeo.seo.sigTitle', 'Título')}
            value={t('blogSeo.seo.charsValue', '{{n}} caracteres', { n: fmt(analysis.meta?.titleLength) })}
            good={(analysis.meta?.titleLength ?? 0) >= 30 && (analysis.meta?.titleLength ?? 0) <= 65}
          />
          <SignalCard
            icon={<FileText className="w-5 h-5" />}
            label={t('blogSeo.seo.sigMetaDesc', 'Meta description')}
            value={t('blogSeo.seo.charsValue', '{{n}} caracteres', { n: fmt(analysis.meta?.descriptionLength) })}
            good={(analysis.meta?.descriptionLength ?? 0) >= 120 && (analysis.meta?.descriptionLength ?? 0) <= 160}
          />
          <SignalCard
            icon={<FileText className="w-5 h-5" />}
            label={t('blogSeo.seo.sigWords', 'Palavras')}
            value={fmt(analysis.content?.wordCount)}
            good={(analysis.content?.wordCount ?? 0) >= 600}
          />
          <SignalCard
            icon={<Code2 className="w-5 h-5" />}
            label={t('blogSeo.seo.sigH1', 'H1')}
            value={fmt(analysis.headings?.h1Count)}
            good={(analysis.headings?.h1Count ?? 0) === 1}
          />
          <SignalCard
            icon={<Link2 className="w-5 h-5" />}
            label={t('blogSeo.seo.sigInternalLinks', 'Links internos')}
            value={fmt(analysis.links?.internalCount)}
            good={(analysis.links?.internalCount ?? 0) > 0}
          />
          <SignalCard
            icon={<ImageIcon className="w-5 h-5" />}
            label={t('blogSeo.seo.sigImagesNoAlt', 'Imagens sem alt')}
            value={fmt(analysis.images?.withoutAlt)}
            good={(analysis.images?.withoutAlt ?? 0) === 0}
          />
          <SignalCard
            icon={<ShieldCheck className="w-5 h-5" />}
            label={t('blogSeo.seo.sigHttps', 'HTTPS')}
            value={analysis.technical?.usesHttps ? t('blogSeo.seo.yes', 'Sim') : t('blogSeo.seo.no', 'Não')}
            good={!!analysis.technical?.usesHttps}
          />
          <SignalCard
            icon={<Smartphone className="w-5 h-5" />}
            label={t('blogSeo.seo.sigViewport', 'Viewport')}
            value={analysis.technical?.hasViewport ? t('blogSeo.seo.yes', 'Sim') : t('blogSeo.seo.no', 'Não')}
            good={!!analysis.technical?.hasViewport}
          />
          <SignalCard
            icon={<Code2 className="w-5 h-5" />}
            label={t('blogSeo.seo.sigSchema', 'Schema')}
            value={analysis.technical?.hasStructuredData ? t('blogSeo.seo.yes', 'Sim') : t('blogSeo.seo.no', 'Não')}
            good={!!analysis.technical?.hasStructuredData}
          />
          <SignalCard
            icon={<Zap className="w-5 h-5" />}
            label={t('blogSeo.seo.sigLoadTime', 'Tempo de carga')}
            value={fmtMs(analysis.loadTime)}
            good={(analysis.loadTime ?? 0) < 3000}
          />
        </div>
      </div>

      {/* Issues */}
      {(analysis.issues?.length ?? 0) > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            {t('blogSeo.seo.issuesTitle', 'Problemas encontrados')}
          </h3>
          <ul className="space-y-2">
            {(analysis.issues ?? []).map((issue, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm">
                {issue.type === 'error' && <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                {issue.type === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />}
                {issue.type === 'info' && <CheckCircle className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />}
                <span className="text-gray-600 dark:text-gray-300">{issue.message}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* AI Action Plan */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-primary-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('blogSeo.seo.planTitle', 'Plano de ação (IA)')}
          </h3>
        </div>
        {(aiPlan?.recommendations?.length ?? 0) > 0 ? (
          <div className="space-y-3">
            {(aiPlan?.recommendations ?? []).map((rec, i) => (
              <Card key={i} className="border-l-4 border-l-primary-500">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white mr-auto">{rec.title}</h4>
                  {impactBadge(rec.impact, t)}
                  {effortBadge(rec.effort, t)}
                  {rec.category && <Badge variant="primary">{rec.category}</Badge>}
                </div>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-600 dark:text-gray-300">
                    <span className="font-medium text-gray-900 dark:text-white">{t('blogSeo.seo.why', 'Por quê:')} </span>
                    {rec.why}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    <span className="font-medium text-gray-900 dark:text-white">{t('blogSeo.seo.how', 'Como:')} </span>
                    {rec.how}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              {t('blogSeo.seo.noPlan', 'Nenhuma recomendação da IA disponível para esta URL.')}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

// ===================== Compare result =====================
type RowDef = {
  label: string;
  get: (a: SeoPageAnalysis) => number;
  render: (a: SeoPageAnalysis) => React.ReactNode;
  higherBetter: boolean;
};

const CompareResult: React.FC<{ row: SeoAnalysisRow }> = ({ row }) => {
  const { t } = useTranslation();
  const site = row.result?.site;
  const comps = row.result?.competitors ?? [];
  const ai = row.result?.aiComparison ?? null;

  if (!site) {
    return (
      <Card>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
          {t('blogSeo.seo.noResult', 'Sem dados de resultado para esta análise.')}
        </p>
      </Card>
    );
  }

  const all = [site, ...comps];

  const boolCell = (v?: boolean) =>
    v ? <span className="text-green-500">✓</span> : <span className="text-red-500">✗</span>;

  const rows: RowDef[] = [
    { label: t('blogSeo.seo.rowScore', 'Score SEO'), get: (a) => a.score ?? 0, render: (a) => fmt(a.score), higherBetter: true },
    { label: t('blogSeo.seo.rowWords', 'Palavras'), get: (a) => a.content?.wordCount ?? 0, render: (a) => fmt(a.content?.wordCount), higherBetter: true },
    { label: t('blogSeo.seo.rowH1', 'H1'), get: (a) => a.headings?.h1Count ?? 0, render: (a) => fmt(a.headings?.h1Count), higherBetter: true },
    { label: t('blogSeo.seo.rowInternalLinks', 'Links internos'), get: (a) => a.links?.internalCount ?? 0, render: (a) => fmt(a.links?.internalCount), higherBetter: true },
    { label: t('blogSeo.seo.rowImagesNoAlt', 'Imagens sem alt'), get: (a) => a.images?.withoutAlt ?? 0, render: (a) => fmt(a.images?.withoutAlt), higherBetter: false },
    { label: t('blogSeo.seo.rowHttps', 'HTTPS'), get: (a) => (a.technical?.usesHttps ? 1 : 0), render: (a) => boolCell(a.technical?.usesHttps), higherBetter: true },
    { label: t('blogSeo.seo.rowViewport', 'Viewport'), get: (a) => (a.technical?.hasViewport ? 1 : 0), render: (a) => boolCell(a.technical?.hasViewport), higherBetter: true },
    { label: t('blogSeo.seo.rowSchema', 'Schema'), get: (a) => (a.technical?.hasStructuredData ? 1 : 0), render: (a) => boolCell(a.technical?.hasStructuredData), higherBetter: true },
    { label: t('blogSeo.seo.rowLoadTime', 'Tempo de carga'), get: (a) => a.loadTime ?? 0, render: (a) => fmtMs(a.loadTime), higherBetter: false },
  ];

  const bestValue = (rd: RowDef): number => {
    const vals = all.map((c) => rd.get(c.analysis));
    let best = vals[0] ?? 0;
    for (const v of vals) {
      if (rd.higherBetter ? v > best : v < best) best = v;
    }
    return best;
  };

  const insightSection = (title: string, icon: React.ReactNode, items: string[] | undefined, accent: string) => (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <span className={accent}>{icon}</span>
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h4>
      </div>
      {(items?.length ?? 0) > 0 ? (
        <ul className="space-y-2">
          {(items ?? []).map((it, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
              <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${accent.replace('text-', 'bg-')}`} />
              {it}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500">{t('blogSeo.seo.noneListed', 'Nada listado.')}</p>
      )}
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Matrix */}
      <Card padding={false} className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left font-semibold text-gray-900 dark:text-white px-4 py-3 whitespace-nowrap">
                  {t('blogSeo.seo.colSignal', 'Sinal')}
                </th>
                <th className="text-left font-semibold text-primary-600 dark:text-primary-400 px-4 py-3 whitespace-nowrap">
                  {t('blogSeo.seo.colYourSite', 'Seu site')}
                  <div className="text-[10px] font-normal text-gray-400 dark:text-gray-500">{truncateUrl(site.url, 22)}</div>
                </th>
                {comps.map((c, i) => (
                  <th key={i} className="text-left font-semibold text-gray-700 dark:text-gray-200 px-4 py-3 whitespace-nowrap">
                    {t('blogSeo.seo.colCompetitor', 'Concorrente {{n}}', { n: i + 1 })}
                    <div className="text-[10px] font-normal text-gray-400 dark:text-gray-500">{truncateUrl(c.url, 22)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((rd, ri) => {
                const best = bestValue(rd);
                return (
                  <tr key={ri} className="border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                    <td className="px-4 py-3 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">{rd.label}</td>
                    {all.map((c, ci) => {
                      const isBest = rd.get(c.analysis) === best;
                      return (
                        <td
                          key={ci}
                          className={`px-4 py-3 whitespace-nowrap ${isBest ? 'font-bold text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-200'}`}
                        >
                          {rd.render(c.analysis)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* AI insights */}
      {ai ? (
        <>
          <div className="rounded-2xl bg-gradient-to-r from-primary-600 to-purple-600 p-5 text-white shadow-lg">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5" />
              <h3 className="text-base font-semibold">{t('blogSeo.seo.verdictTitle', 'Veredito da IA')}</h3>
            </div>
            <p className="text-sm text-white/90 leading-relaxed">{ai.verdict}</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {insightSection(t('blogSeo.seo.youWin', 'Você ganha'), <Trophy className="w-5 h-5" />, ai.youWin, 'text-green-500')}
            {insightSection(t('blogSeo.seo.youLose', 'Você perde'), <AlertTriangle className="w-5 h-5" />, ai.youLose, 'text-red-500')}
            {insightSection(t('blogSeo.seo.opportunities', 'Oportunidades'), <Target className="w-5 h-5" />, ai.opportunities, 'text-indigo-500')}
            {insightSection(t('blogSeo.seo.quickWins', 'Quick wins'), <Zap className="w-5 h-5" />, ai.quickWins, 'text-amber-500')}
          </div>
        </>
      ) : (
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
            {t('blogSeo.seo.noInsights', 'Nenhum insight competitivo da IA disponível.')}
          </p>
        </Card>
      )}
    </div>
  );
};

// ===================== Result router =====================
const ResultPanel: React.FC<{ row: SeoAnalysisRow | null; empty: React.ReactNode }> = ({ row, empty }) => {
  if (!row) return <>{empty}</>;
  if (row.status === 'running') return <RunningPanel />;
  if (row.status === 'failed') return <FailedPanel message={row.error_message} />;
  return row.type === 'compare' ? <CompareResult row={row} /> : <SiteResult row={row} />;
};

// ===================== Analysis tab =====================
const AnalysisTab: React.FC = () => {
  const { t } = useTranslation();
  const tFb = useCallback((k: string, fb: string) => t(k, fb), [t]);
  const store = useSeoAnalyses('site', tFb);
  const [url, setUrl] = useState('');

  const run = async () => {
    const v = url.trim();
    if (!v) {
      toast.error(t('blogSeo.seo.errEnterUrl', 'Digite uma URL'));
      return;
    }
    const ok = await store.submit(() => blogService.seoSite(v));
    if (ok) {
      setUrl('');
      toast.success(t('blogSeo.seo.analysisStarted', 'Análise iniciada — roda em segundo plano.'));
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-start gap-3 mb-4">
          <Sparkles className="w-5 h-5 text-primary-500 mt-0.5 shrink-0" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('blogSeo.seo.analysisIntro', 'Analise o SEO on-page de qualquer URL e receba um plano de ação gerado por IA. A análise roda em segundo plano e fica salva no histórico.')}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('blogSeo.seo.urlPlaceholder', 'https://seusite.com')}
            className="flex-1"
          />
          <Button variant="primary" onClick={run} isLoading={store.submitting} leftIcon={<Search className="w-4 h-4" />}>
            {t('blogSeo.seo.analyze', 'Analisar')}
          </Button>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
          <HistoryList
            rows={store.history}
            selectedId={store.selected?.id}
            loading={store.loadingHistory}
            onSelect={store.select}
            onDelete={store.remove}
            onRefresh={store.loadHistory}
            emptyLabel={t('blogSeo.seo.historyEmptyAnalysis', 'Nenhuma análise ainda.')}
          />
        </div>
        <div className="lg:col-span-2">
          <ResultPanel
            row={store.selected}
            empty={
              <EmptyPanel
                icon={<Search className="w-8 h-8 text-primary-500" />}
                title={t('blogSeo.seo.emptyAnalysisTitle', 'Digite uma URL para começar')}
                hint={t('blogSeo.seo.emptyAnalysisHint', 'Veja sinais on-page, problemas e um plano de ação inteligente.')}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

// ===================== Competitors tab =====================
const CompetitorsTab: React.FC = () => {
  const { t } = useTranslation();
  const tFb = useCallback((k: string, fb: string) => t(k, fb), [t]);
  const store = useSeoAnalyses('compare', tFb);
  const [url, setUrl] = useState('');
  const [compInput, setCompInput] = useState('');
  const [competitors, setCompetitors] = useState<string[]>([]);

  const addCompetitor = () => {
    const v = compInput.trim();
    if (!v) return;
    if (competitors.length >= 3) {
      toast.error(t('blogSeo.seo.errMaxCompetitors', 'Máximo de 3 concorrentes'));
      return;
    }
    if (competitors.includes(v)) {
      toast.error(t('blogSeo.seo.errDuplicate', 'Concorrente já adicionado'));
      return;
    }
    setCompetitors([...competitors, v]);
    setCompInput('');
  };

  const removeCompetitor = (c: string) => setCompetitors(competitors.filter((x) => x !== c));

  const run = async () => {
    const v = url.trim();
    if (!v) {
      toast.error(t('blogSeo.seo.errEnterUrl', 'Digite uma URL'));
      return;
    }
    if (competitors.length === 0) {
      toast.error(t('blogSeo.seo.errNeedCompetitor', 'Adicione ao menos 1 concorrente'));
      return;
    }
    const ok = await store.submit(() => blogService.seoCompare(v, competitors));
    if (ok) {
      toast.success(t('blogSeo.seo.compareStarted', 'Comparação iniciada — roda em segundo plano.'));
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-start gap-3 mb-4">
          <Swords className="w-5 h-5 text-primary-500 mt-0.5 shrink-0" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('blogSeo.seo.competitorsIntro', 'Compare seu site com até 3 concorrentes, lado a lado, e receba insights competitivos da IA. Roda em segundo plano e fica salvo no histórico.')}
          </p>
        </div>
        <div className="space-y-3">
          <Input
            label={t('blogSeo.seo.yourSite', 'Seu site')}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('blogSeo.seo.urlPlaceholder', 'https://seusite.com')}
          />
          <div>
            <label className="label">{t('blogSeo.seo.competitorsLabel', 'Concorrentes (máx. 3)')}</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                value={compInput}
                onChange={(e) => setCompInput(e.target.value)}
                placeholder={t('blogSeo.seo.competitorPlaceholder', 'https://concorrente.com')}
                className="flex-1"
              />
              <Button variant="secondary" onClick={addCompetitor} leftIcon={<Plus className="w-4 h-4" />}>
                {t('blogSeo.seo.add', 'Adicionar')}
              </Button>
            </div>
            {competitors.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {competitors.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800">
                    {truncateUrl(c)}
                    <button onClick={() => removeCompetitor(c)} className="hover:text-red-500">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="primary" onClick={run} isLoading={store.submitting} leftIcon={<Swords className="w-4 h-4" />}>
              {t('blogSeo.seo.compare', 'Comparar')}
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
          <HistoryList
            rows={store.history}
            selectedId={store.selected?.id}
            loading={store.loadingHistory}
            onSelect={store.select}
            onDelete={store.remove}
            onRefresh={store.loadHistory}
            emptyLabel={t('blogSeo.seo.historyEmptyCompare', 'Nenhuma comparação ainda.')}
          />
        </div>
        <div className="lg:col-span-2">
          <ResultPanel
            row={store.selected}
            empty={
              <EmptyPanel
                icon={<Swords className="w-8 h-8 text-primary-500" />}
                title={t('blogSeo.seo.emptyCompetitorsTitle', 'Adicione concorrentes para comparar')}
                hint={t('blogSeo.seo.emptyCompetitorsHint', 'Veja uma matriz lado a lado e insights competitivos da IA.')}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

// ===================== Root =====================
const SeoStudio: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<SubTab>('analysis');

  const tabs: { id: SubTab; label: string; desc: string; icon: React.ReactNode }[] = [
    {
      id: 'audit',
      label: t('blogSeo.seo.tabAudit', 'Auditoria técnica'),
      desc: t('blogSeo.seo.descAudit', 'Performance, Core Web Vitals e nota PageSpeed do Google (Lighthouse).'),
      icon: <Gauge className="w-4 h-4" />,
    },
    {
      id: 'analysis',
      label: t('blogSeo.seo.tabAnalysis', 'Análise on-page (IA)'),
      desc: t('blogSeo.seo.descAnalysis', 'Meta tags, conteúdo e estrutura da SUA página + plano de ação priorizado pela IA.'),
      icon: <Sparkles className="w-4 h-4" />,
    },
  ];

  const activeDesc = tabs.find((tb) => tb.id === tab)?.desc ?? '';

  return (
    <div className="space-y-6">
      {/* Hero header */}
      <div className="rounded-2xl bg-gradient-to-r from-primary-600 to-purple-600 p-6 text-white shadow-lg">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 backdrop-blur">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">{t('blogSeo.seo.title', 'SEO Studio')}</h2>
            <p className="text-sm text-white/80">{t('blogSeo.seo.subtitle', 'Audite seu site e analise o SEO da página com IA')}</p>
          </div>
        </div>
      </div>

      {/* Sub-tab pills */}
      <div>
        <div className="flex flex-wrap gap-2">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                tab === tb.id
                  ? 'bg-gradient-to-r from-primary-600 to-purple-600 text-white shadow-md'
                  : 'border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {tb.icon}
              {tb.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">{activeDesc}</p>
      </div>

      {/* Keep tab components mounted-on-demand; each rebuilds state from backend on mount. */}
      {tab === 'audit' && <SiteAuditPage />}
      {tab === 'analysis' && <AnalysisTab />}
    </div>
  );
};

export default SeoStudio;
